import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import Turnstile from "react-turnstile";
import {
  Box,
  Button,
  FeedbackButton,
  HStack,
  theme,
  useDevice,
  VStack,
} from "@bpba/ui-components";
import {
  BPFormProvider,
  ErrorBox,
  Form,
  Input,
  transforms,
  useCaptcha,
  useSubmit,
} from "@bpba/form-components";
import { SafeModalInfo } from "./SafeModalInfo";
import { useTurnero } from "../../../context/TurneroContext";
import { DEFAULT_ERROR, getRawExceptionMessage } from "../../../shared/utils/apiError";
import { ReactComponent as PiaLogo } from "../assets/pia.svg";
import QrWhatsapp from "../assets/qr-whatsapp.png";
import { ReactComponent as WhatsAppIcon } from "../assets/whatsapp-icon.svg";

interface Props {
  show: boolean;
  onContinue: () => void;
}

type View = "disuasion" | "asistente";

const WA_NUMBER = "+5491128216222";
const WA_LINK = "https://wa.me/541128216222";

export const InfoTurneroModal = ({ show, onContinue }: Props) => {
  const [view, setView] = useState<View>("disuasion");

  // Reset al cerrar para que la proxima apertura arranque en disuasion.
  useEffect(() => {
    if (!show) {
      const t = setTimeout(() => setView("disuasion"), 280);
      return () => clearTimeout(t);
    }
  }, [show]);

  return (
    <TitleOverride>
      <WhiteFormStyles />
      <SafeModalInfo
        open={show}
        title="Turnos Web"
        onClose={onContinue}
        bg="primary.0"
        color="white"
        maxWidth={
          view === "asistente"
            ? ["100%", null, "470px"]
            : ["100%", null, "700px"]
        }
        boxShadow={
          view === "asistente"
            ? "0 24px 60px rgba(60, 60, 59, 0.22)"
            : undefined
        }
        padding={["29px", "40px"]}
        footer={
          view === "disuasion" ? (
            <SolicitarBtn
              type="button"
              variant="transparent"
              color="white"
              fontWeight="semibold"
              width="fit-content"
              style={{ boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,0.7)" }}
              onClick={() => setView("asistente")}
            >
              Gestionar turno
            </SolicitarBtn>
          ) : null
        }
      >
        {view === "disuasion" ? (
          <DisuasionView />
        ) : (
          <AsistenteView onDone={onContinue} />
        )}
      </SafeModalInfo>
    </TitleOverride>
  );
};

const DisuasionView = () => (
  <VStack gap={4}>
    <p
      style={{
        margin: 0,
        fontSize: theme.fontSizes[2],
        lineHeight: 1.45,
        color: "white",
      }}
    >
      ¡Siempre es mejor ir con turno a la sucursal! Antes de sacarlo, te
      invitamos a revisar estas gestiones que podés hacer online, en pocos
      pasos:
    </p>

    <VStack gap={2}>
      <OnlineLink
        href="https://www.bancoprovincia.com.ar/CDN/Get/CONSULTA_RESUMEN_BIP"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>Necesito ver el resumen de mi tarjeta</span>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          width="auto"
          fontSize={1}
          fontWeight="semibold"
          opacity={0.9}
          flexShrink={0}
        >
          + Info
        </Box>
      </OnlineLink>
      <OnlineLink
        href="https://www.bancoprovincia.com.ar/CDN/Get/DESBLOQUEO_DE_CLAVE"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>Quiero blanquear mis claves</span>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          width="auto"
          fontSize={1}
          fontWeight="semibold"
          opacity={0.9}
          flexShrink={0}
        >
          + Info
        </Box>
      </OnlineLink>
      <OnlineLink
        href="https://www.bancoprovincia.com.ar/CDN/Get/HABILITACION_DE_TARJETA_DE_DEBITO"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>Quiero habilitar mi tarjeta de débito</span>
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          width="auto"
          fontSize={1}
          fontWeight="semibold"
          opacity={0.9}
          flexShrink={0}
        >
          + Info
        </Box>
      </OnlineLink>
    </VStack>

    <p
      style={{
        margin: 0,
        fontSize: theme.fontSizes[2],
        lineHeight: 1.45,
        color: "white",
      }}
    >
      Recordá que podés ingresar a nuestros canales de atención para resolver
      estas y otras consultas:
    </p>

    <ChannelsGrid>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        gap={`${theme.space[3]}px`}
        p="24px"
        borderRadius="8px"
        backgroundColor="rgba(255, 255, 255, 0.1)"
        height="100%"
      >
        <BrandSlot>
          <PiaLogo aria-label="PIA" role="img" />
        </BrandSlot>
        <p
          style={{
            margin: 0,
            fontSize: theme.fontSizes[1],
            lineHeight: 1.45,
            color: "white",
            opacity: 0.9,
          }}
        >
          Nuestro asistente virtual, disponible en todos los canales digitales
          para resolver tus consultas de forma ágil y segura.
        </p>
      </Box>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="stretch"
        gap={`${theme.space[4]}px`}
        p={`${theme.space[5]}px`}
        borderRadius="8px"
        backgroundColor="rgba(255, 255, 255, 0.1)"
      >
        <QrImg src={QrWhatsapp} alt={`QR para escribir al ${WA_NUMBER}`} />
        <VStack justifyContent="space-between" flex={1}>
          <p
            style={{
              margin: 0,
              fontSize: "15px",
              lineHeight: 1.3,
              color: "white",
              opacity: 0.9,
            }}
          >
            Autogestioná tus consultas las 24hs y desde donde estés.
          </p>
          <HStack alignItems="center" gap={`${theme.space[2]}px`}>
            <HideOnMobile>
              <Box
                display="inline-flex"
                width="24px"
                height="24px"
                flexShrink={0}
              >
                <WhatsAppIcon
                  aria-label="WhatsApp"
                  role="img"
                  style={{ width: "100%", height: "100%" }}
                />
              </Box>
            </HideOnMobile>
            <WhatsLink href={WA_LINK} target="_blank" rel="noopener noreferrer">
              11-2821-6222
            </WhatsLink>
          </HStack>
        </VStack>
      </Box>
    </ChannelsGrid>

    <Box pt={2} borderTop="1px solid rgba(255, 255, 255, 0.18)">
      <p
        style={{
          margin: 0,
          fontSize: theme.fontSizes[1],
          lineHeight: 1.45,
          color: "white",
          opacity: 0.85,
        }}
      >
        Si tu trámite requiere atención personalizada, te recomendamos solicitar
        un turno para realizar tus operaciones.
      </p>
    </Box>
  </VStack>
);

const AsistenteView = ({ onDone }: { onDone: () => void }) => {
  const {
    verificarCliente,
    validarReprogramar,
    setDocumento,
    mostrarTurnoVigente,
    setStep,
    sessionToken,
    hasSessionToken,
  } = useTurnero();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const device = useDevice();
  const isMobile = device === "mobile";
  const { captchaToken, setCaptchaToken, resetCaptcha, isCaptchaValid } =
    useCaptcha();
  const { submit, btnState, error, clearError } = useSubmit({
    fallback: DEFAULT_ERROR,
  });
  const methods = useForm<{ doc: string }>({ defaultValues: { doc: "" } });

  const onSubmit = methods.handleSubmit(({ doc }) => {
    if (!hasSessionToken && !isCaptchaValid) return;
    submit(async () => {
      const c = await verificarCliente(doc, captchaToken, sessionToken ?? '');
      setDocumento(doc);
      if (c.turnoWeb) {
        const turnoWeb = c.turnoWeb;
        let puedeReprogramar = true;
        try {
          await validarReprogramar(turnoWeb);
        } catch (validErr: unknown) {
          const validMsg = getRawExceptionMessage(validErr);
          if (/reprogramad[oa]/i.test(validMsg)) puedeReprogramar = false;
        }
        onDone();
        // Diferimos para que el fade-out del modal verde termine antes de
        // que aparezca el "Ya tenés un turno vigente" — sin cleanup en
        // useEffect, asi el setTimeout no se cancela al desmontarse esta
        // vista (que pasa apenas onDone setea show=false).
        setTimeout(() => {
          mostrarTurnoVigente(doc, turnoWeb, puedeReprogramar);
        }, 280);
      } else {
        setStep("tramite");
        if (pathname !== "/" && pathname !== "/solicitar") navigate("/");
        onDone();
      }
      resetCaptcha();
    });
  });

  return (
    <BPFormProvider
      methods={methods}
      rules={{
        doc: {
          required: "Ingresá un CUIT, CUIL o DNI válido.",
          pattern: {
            value: /^\d{7,11}$/,
            message: "Ingresá un CUIT, CUIL o DNI válido.",
          },
        },
      }}
    >
      <Form
        id="asistente-dni-form"
        onSubmit={onSubmit}
        flexDirection="column"
        gap={[3, null, 4]}
        pt={[2, null, 3]}
      >
        <p
          style={{
            margin: 0,
            fontSize: theme.fontSizes[2],
            lineHeight: 1.45,
            color: "white",
          }}
        >
          Ingresá tus datos y gestioná tu turno.
        </p>

        <Box
          width="100%"
          maxWidth={["100%", null, "390px"]}
          mx="auto"
          minWidth={0}
          display="flex"
          flexDirection="column"
          gap={`${theme.space[3]}px`}
        >
          <Box
            display="flex"
            flexDirection={["column", "row"]}
            alignItems={["stretch", "flex-start"]}
            gap={`${theme.space[3]}px`}
            width="100%"
          >
            <Box flex={[null, "1"]} minWidth={0} width={["100%", "auto"]}>
              <Input
                name="doc"
                label="CUIT / CUIL / DNI"
                type="text"
                inputMode="numeric"
                placeholder="Ej: 20123456789"
                maxLength={11}
                setValueAs={transforms.digitsOnly}
                width="100%"
                size={['lg', 'lg', 'lg', 'md'] as any}
              />
            </Box>
            <FeedbackSolicitarBtn
              type="submit"
              variant="transparent"
              color="white"
              fontWeight="semibold"
              btnState={btnState}
              text="Continuar"
              size={["lg", "lg", "lg", "md"]}
              width={["100%", "auto"]}
              withLabelSpacer={!isMobile}
              style={{ boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,0.7)" }}
              disabled={
                btnState === "loading" || (!hasSessionToken && !isCaptchaValid)
              }
            />
          </Box>
          {error && (
            <ErrorBox mt="0" onClose={clearError}>
              {error}
            </ErrorBox>
          )}
          {hasSessionToken ? (
            <Box mt={6} pb={5} />
          ) : (
            <Box display="flex" justifyContent="center" mt={6}>
              <Turnstile
                sitekey={process.env.REACT_APP_TURNSTILE_KEY!}
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken("")}
                appearance="always"
                refreshExpired="auto"
                theme="light"
                size="normal"
              />
            </Box>
          )}
        </Box>
      </Form>
    </BPFormProvider>
  );
};

// Forzamos blanco en todo lo que esta dentro del asistente form (labels,
// helper text, ErrorBox del input). El Input en si tiene fondo blanco con
// texto oscuro — eso queda asi para legibilidad.
const WhiteFormStyles = createGlobalStyle`
  #asistente-dni-form label,
  #asistente-dni-form p { color: ${theme.colors.white}; }
  /* El input default tiene border 2px solid secondary[0] (gris claro) que
     sobre el verde del modal se ve como un borde mal recortado. Lo bajamos
     a un borde sutil que armoniza con el fondo blanco del input. */
  #asistente-dni-form input {
    border-color: transparent !important;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
  }
  #asistente-dni-form input:focus-visible {
    border-color: ${theme.colors.primary[0]} !important;
    box-shadow: none;
  }
  /* ErrorBox hereda color:white del modal verde — forzamos el color correcto */
  #asistente-dni-form [role="alert"] > div {
    color: ${theme.colors.error.text} !important;
  }
`;

const TitleOverride = styled.div({
  '& h1[id^="modal-title"]': { color: theme.colors.white },
  '& button[aria-label="Cerrar"], & button[aria-label*="close" i]': {
    color: theme.colors.white,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.15) !important",
      color: `${theme.colors.white} !important`,
    },
  },
});

const HideOnMobile = styled.div({
  "@media (max-width: 767px)": { display: "none" },
});

const ChannelsGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: `${theme.space[2]}px`,
  "@media (max-width: 420px)": { gridTemplateColumns: "1fr" },
});

const OnlineLink = styled.a({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: `${theme.space[3]}px`,
  padding: `${theme.space[3]}px ${theme.space[4]}px`,
  borderRadius: "8px",
  background: "rgba(255, 255, 255, 0.12)",
  color: theme.colors.white,
  fontSize: `${theme.fontSizes[2]}px`,
  lineHeight: 1.5,
  fontWeight: theme.fontWeights.medium,
  textDecoration: "none",
  transition: "background 0.18s ease, transform 0.18s ease",
  "&:visited, &:link": { color: theme.colors.white },
  ":hover": { background: "rgba(255, 255, 255, 0.2)" },
  ":active": { transform: "scale(0.99)" },
});

const BrandSlot = styled.div({
  display: "flex",
  alignItems: "center",
  color: theme.colors.white,
  width: 110,
  height: 32,
  "& svg": {
    maxWidth: "100%",
    maxHeight: "100%",
    width: "auto",
    height: "auto",
    display: "block",
  },
  "@media (min-width: 768px)": {
    width: 160,
    height: 48,
  },
});

const WhatsLink = styled.a({
  color: theme.colors.white,
  fontWeight: theme.fontWeights.semibold,
  fontSize: `${theme.fontSizes[2]}px`,
  textDecoration: "underline",
  whiteSpace: "nowrap",
});

const SolicitarBtn = styled(Button)({
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.15) !important",
    color: `${theme.colors.white} !important`,
  },
});

const FeedbackSolicitarBtn = styled(FeedbackButton)({
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.15) !important",
    color: `${theme.colors.white} !important`,
  },
});

const QrImg = styled.img({
  display: "block",
  flexShrink: 0,
  width: "auto",
  height: "100%",
  maxHeight: "90px",
  minHeight: "70px",
  alignSelf: "stretch",
  imageRendering: "pixelated",
  "@media (min-width: 768px)": {
    maxHeight: "140px",
    minHeight: "80px",
  },
});
